import * as React from "react";

import type {
  BedrockCatalogModel,
  BulkOrgModelConfigResponse,
} from "@/shared/domain/orgs";
import {
  Dialog,
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";

import styles from "./AiModels.module.css";
import { BulkActionApply, BulkResultSummary } from "./BulkActionApply";

interface RemoveModelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The orgs to remove the model from, as targeted when the dialog opened. */
  orgIds: readonly string[];
  /** The catalog's chat models; any of them can be removed, verified or not. */
  chatModels: readonly BedrockCatalogModel[];
  /** Fired when the dialog closes after the change was applied. */
  onApplied: () => void;
}

/**
 * Remove one chat model from the targeted orgs: pick the model and apply,
 * then see who was updated, who was skipped, and whose default moved.
 */
export function RemoveModelDialog({
  open,
  onOpenChange,
  orgIds,
  chatModels,
  onApplied,
}: RemoveModelDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className={styles.dialog}>
        {open && (
          <RemoveModelBody
            // A fresh body per opening: new target, no earlier result.
            orgIds={orgIds}
            chatModels={chatModels}
            onClose={(applied) => {
              onOpenChange(false);
              if (applied) {
                onApplied();
              }
            }}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function RemoveModelBody({
  orgIds: initialOrgIds,
  chatModels,
  onClose,
}: {
  orgIds: readonly string[];
  chatModels: readonly BedrockCatalogModel[];
  onClose: (applied: boolean) => void;
}) {
  // Snapshot the target: the page may clear its selection behind the dialog.
  const [orgIds] = React.useState(() => [...initialOrgIds]);
  const [profileId, setProfileId] = React.useState<string | null>(null);
  const [applied, setApplied] =
    React.useState<BulkOrgModelConfigResponse | null>(null);

  if (applied !== null) {
    return (
      <div className={styles.dialogBody}>
        <Dialog.Title>Model removed</Dialog.Title>
        <BulkResultSummary result={applied} verb="Updated" />
        <div className={styles.dialogActions}>
          <RobotoButton onClick={() => onClose(true)}>Done</RobotoButton>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dialogBody}>
      <Dialog.Title>
        Remove a model from {orgIds.length}{" "}
        {orgIds.length === 1 ? "org" : "orgs"}
      </Dialog.Title>
      <p className={styles.note}>
        The model is taken off each org&apos;s list. Where it was the default,
        the next chat model on the list becomes the default. Orgs without it, or
        with no other chat model, are skipped.
      </p>
      {/* A radio list rather than a Select: the Select popup layers below
          the Dialog, and the catalog is short enough to show whole. */}
      <RadioGroup<string>
        legend="Model"
        value={profileId}
        onValueChange={setProfileId}
      >
        {chatModels.map((model) => (
          <Radio<string> key={model.id} value={model.id} label={model.label} />
        ))}
      </RadioGroup>
      <BulkActionApply
        orgIds={orgIds}
        action={
          profileId === null
            ? null
            : { type: "remove_model", profile_id: profileId }
        }
        onApplied={setApplied}
        secondaryAction={
          <RobotoButton variant="ghost" onClick={() => onClose(false)}>
            Cancel
          </RobotoButton>
        }
      />
    </div>
  );
}
